/**
 * Pure mesh-to-attribute-array conversion for a baked model LOD (see
 * `packages/baker/src/model-bake.ts`) — no three.js import, no DOM, fully
 * unit-testable without a GPU. `ModelViewer.tsx` feeds this module's output
 * straight into a three.js `BufferGeometry`'s `setAttribute` calls.
 *
 * Red Baron's geometry is flat-shaded (one colour per *face*, spec
 * docs/formats/models-tbl.md §4.4), not per-vertex like a modern asset. The
 * simplest correct way to get flat per-face colour *and* flat per-face
 * shading out of a single indexed vertex buffer would require duplicating
 * every vertex that's shared by two differently-coloured faces anyway — so
 * this builds a **non-indexed** buffer instead: every triangle gets its own
 * 3 vertices, each carrying that triangle's colour and normal. Simpler than
 * juggling material groups per unique colour, and correct by construction.
 */

import { DECAL_LAYER_OFFSET_M, decalLayers } from "./decal-layers.ts";

type Corner = readonly [number, number, number];

/**
 * One baked face's colour (`packages/baker`'s `ModelFaceColor`). `colorRgba` is
 * what the original draws at the bake's reference attitude and is all this
 * module needs; the three ramp fields ride along for `ramp-shade.ts`, which
 * re-derives the colour per frame. All three are optional so a `data/baked`
 * from before shading-and-paint.md §9.3 still builds — it just cannot re-shade.
 */
export interface BakedModelFaceColor {
  readonly colorRgba: readonly [number, number, number, number];
  readonly anchorIndex?: number;
  readonly halfWidth?: number;
  readonly shadeable?: boolean;
}

/** The subset of a baked model LOD's JSON this module needs — see `BakedModelLod` in `packages/baker`. */
export interface BakedModelLodMesh {
  readonly positions: readonly number[];
  readonly indices: readonly number[];
  readonly faceColors: readonly BakedModelFaceColor[];
  readonly faceNormals: readonly (readonly [number, number, number] | null)[];
}

export interface GeometryAttributes {
  /** Flat x,y,z per vertex, 3 per triangle-corner, non-indexed. */
  readonly positions: Float32Array;
  /** Flat x,y,z per vertex, unit length — the face's own normal if the source had one, else a computed geometric normal. */
  readonly normals: Float32Array;
  /** Flat r,g,b per vertex in [0,1] — three.js `vertexColors` convention. */
  readonly colors: Float32Array;
  readonly triangleCount: number;
}

const RGB_MAX = 255;

/** Computes a right-handed geometric face normal from a triangle's 3 corners; returns `[0,0,1]` for a degenerate (zero-area) triangle. */
export function computeFaceNormal(
  p0: readonly [number, number, number],
  p1: readonly [number, number, number],
  p2: readonly [number, number, number],
): readonly [number, number, number] {
  const ux = p1[0] - p0[0];
  const uy = p1[1] - p0[1];
  const uz = p1[2] - p0[2];
  const vx = p2[0] - p0[0];
  const vy = p2[1] - p0[1];
  const vz = p2[2] - p0[2];

  const nx = uy * vz - uz * vy;
  const ny = uz * vx - ux * vz;
  const nz = ux * vy - uy * vx;
  const length = Math.hypot(nx, ny, nz);
  if (length === 0) return [0, 0, 1];
  return [nx / length, ny / length, nz / length];
}

/**
 * Expands an indexed, per-face-coloured baked LOD mesh into flat,
 * non-indexed `position`/`normal`/`color` attribute arrays. Faces missing a
 * source normal (spec §4.4's `op === 0x80`) get a computed geometric
 * normal instead of a zero vector, so flat shading still looks reasonable.
 */
export function buildGeometryAttributes(mesh: BakedModelLodMesh): GeometryAttributes {
  const triangleCount = mesh.indices.length / 3;
  if (!Number.isInteger(triangleCount)) {
    throw new Error(`buildGeometryAttributes: indices.length (${mesh.indices.length}) is not a multiple of 3`);
  }
  if (mesh.faceColors.length !== triangleCount || mesh.faceNormals.length !== triangleCount) {
    throw new Error(
      `buildGeometryAttributes: faceColors (${mesh.faceColors.length}) / faceNormals (${mesh.faceNormals.length}) must have one entry per triangle (${triangleCount})`,
    );
  }

  const positions = new Float32Array(triangleCount * 9);
  const normals = new Float32Array(triangleCount * 9);
  const colors = new Float32Array(triangleCount * 9);

  const triangleCorners: (readonly [Corner, Corner, Corner])[] = [];
  const triangleNormals: Corner[] = [];

  const vertexOf = (index: number): readonly [number, number, number] => {
    const p = index * 3;
    const x = mesh.positions[p];
    const y = mesh.positions[p + 1];
    const z = mesh.positions[p + 2];
    if (x === undefined || y === undefined || z === undefined) {
      throw new Error(`buildGeometryAttributes: vertex index ${index} out of bounds (positions has ${mesh.positions.length / 3} vertices)`);
    }
    return [x, y, z];
  };

  for (let t = 0; t < triangleCount; t++) {
    const i0 = mesh.indices[t * 3]!;
    const i1 = mesh.indices[t * 3 + 1]!;
    const i2 = mesh.indices[t * 3 + 2]!;
    const p0 = vertexOf(i0);
    const p1 = vertexOf(i1);
    const p2 = vertexOf(i2);

    const sourceNormal = mesh.faceNormals[t]!;
    const [nx, ny, nz] = sourceNormal ?? computeFaceNormal(p0, p1, p2);
    triangleCorners.push([p0, p1, p2]);
    triangleNormals.push([nx, ny, nz]);

    const [r, g, b] = mesh.faceColors[t]!.colorRgba;
    const cr = r / RGB_MAX;
    const cg = g / RGB_MAX;
    const cb = b / RGB_MAX;

    const base = t * 9;
    const corners = [p0, p1, p2];
    for (let c = 0; c < 3; c++) {
      const corner = corners[c]!;
      const o = base + c * 3;
      positions[o] = corner[0];
      positions[o + 1] = corner[1];
      positions[o + 2] = corner[2];
      normals[o] = nx;
      normals[o + 1] = ny;
      normals[o + 2] = nz;
      colors[o] = cr;
      colors[o + 1] = cg;
      colors[o + 2] = cb;
    }
  }

  const layers = decalLayers(triangleCorners, triangleNormals);
  for (let t = 0; t < triangleCount; t++) {
    const layer = layers[t]!;
    if (layer === 0) continue;
    const [nx, ny, nz] = triangleNormals[t]!;
    const nudge = layer * DECAL_LAYER_OFFSET_M;
    const base = t * 9;
    for (let c = 0; c < 3; c++) {
      const o = base + c * 3;
      positions[o] = positions[o]! + nx * nudge;
      positions[o + 1] = positions[o + 1]! + ny * nudge;
      positions[o + 2] = positions[o + 2]! + nz * nudge;
    }
  }

  return { positions, normals, colors, triangleCount };
}
